import React from 'react';
import './Blog.css';

const checklistItems = [
  {
    title: 'Choose insulated outerwear',
    body: 'Freezer jackets and insulated workwear are essential for cold storage warehouses, outdoor sites, and other low-temperature environments. Totalguard supplies below-zero double-lined jackets and trousers, hi-viz two-tone freezer jackets, bunny jackets, and softshell jackets built for warmth, wind resistance, and extended use.'
  },
  {
    title: 'Build a thermal layering system',
    body: 'Effective winter PPE starts with layers. Thermal underwear helps retain body heat and wick moisture, while outer layers protect against wind and water exposure. This keeps workers warm without overheating or discomfort during long shifts.'
  },
  {
    title: 'Keep visibility high',
    body: 'Winter conditions often mean reduced daylight, poor weather, and lower visibility. High-visibility winter workwear with reflective elements helps teams stay seen without sacrificing insulation.'
  },
  {
    title: 'Prioritize durability',
    body: 'High-quality protective clothing with reinforced stitching, water-resistant outer layers, and reliable zippers reduces replacement costs and supports long-term performance in demanding conditions.'
  }
];

const ppeBenefits = [
  'Reduce workplace injuries',
  'Improve productivity',
  'Minimise costly downtime',
  'Support compliance and workplace safety standards',
  'Build a stronger safety culture'
];

const ppeSelectionFactors = [
  'Workplace hazards',
  'Comfort and fit',
  'Durability and quality',
  'Safety compliance standards',
  'South African weather conditions',
  'Ease of maintenance and replacement'
];

const safetyPractices = [
  'Regular safety training',
  'PPE inspections and maintenance',
  'Clear workplace safety procedures',
  'Easy access to protective equipment'
];

const Blog = () => {
  return (
    <section id="blog" className="blog section">
      <div className="container">
        <div className="blog-header animate-fade-in-up">
          <p className="blog-kicker">Totalguard Blog</p>
          <h2 className="section-title">Workwear & PPE Insights</h2>
          <p className="blog-subtitle">
            Practical guidance for safer worksites, better PPE decisions, and dependable workforce
            protection.
          </p>
        </div>

        <div className="blog-posts">
          <article className="blog-article animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <div className="blog-article-heading">
              <p className="blog-post-label">Winter Workwear</p>
              <h3>Winter Workwear Checklist</h3>
            </div>

            <div className="blog-intro">
              <p>
                Cold environments demand specialized winter workwear. Standard PPE is not designed
                for low temperatures, and using the wrong gear can reduce productivity, increase
                safety risks, and lead to non-compliance.
              </p>
              <p>
                The purpose of cold weather PPE is clear: maintain body temperature, support
                mobility, and provide protection without compromising performance. Every item should
                be selected based on real working conditions, not convenience.
              </p>
            </div>

            <div className="blog-checklist" aria-label="Winter workwear checklist">
              {checklistItems.map((item, index) => (
                <section className="blog-checklist-item" key={item.title}>
                  <span className="blog-check-icon" aria-hidden="true"></span>
                  <div>
                    <p className="blog-step">Checklist item {index + 1}</p>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                </section>
              ))}
            </div>

            <div className="blog-closing">
              <p>
                A complete winter PPE strategy is not just about meeting minimum requirements. It is
                about ensuring your workforce operates efficiently in cold environments without
                interruption. If your current gear does not meet these standards, it is already
                costing you in performance and risk.
              </p>
              <p>
                Upgrade to high-performance winter workwear from Totalguard and ensure your team
                stays protected, compliant, and productive in cold conditions.
              </p>
              <a href="#contact" className="btn">Discuss Winter PPE</a>
            </div>
          </article>

          <article className="blog-article animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <div className="blog-article-heading">
              <p className="blog-post-label">Workplace Safety</p>
              <h3>Why Quality PPE Matters on Worksites</h3>
            </div>

            <div className="blog-content">
              <p>
                Safety on the job is not a luxury, it is essential for protecting workers, reducing
                downtime, and maintaining productivity. Across construction sites, factories,
                warehouses, and mining operations in South Africa, the right Personal Protective
                Equipment (PPE) helps create safer and more efficient workplaces.
              </p>

              <section className="blog-section">
                <h4>What Is PPE?</h4>
                <p>
                  PPE refers to protective gear designed to reduce workplace risks and injuries.
                  Common PPE includes safety boots, hard hats, reflective workwear, gloves,
                  respirators, protective eyewear, and hearing protection. Each item is designed to
                  protect workers from specific hazards in demanding environments.
                </p>
              </section>

              <section className="blog-section">
                <h4>Why PPE Matters</h4>
                <p>Quality PPE helps businesses:</p>
                <ul className="blog-list">
                  {ppeBenefits.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p>
                  Investing in reliable PPE protects both employees and business operations.
                </p>
              </section>

              <section className="blog-section">
                <h4>Choosing PPE Your Team Will Actually Wear</h4>
                <p>
                  Not all PPE is created equal. Workers are more likely to wear equipment that is
                  comfortable, durable, and suited to their environment. When selecting PPE, consider:
                </p>
                <ul className="blog-list blog-list-columns">
                  {ppeSelectionFactors.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p>
                  Involving workers in the selection process can also improve comfort and overall
                  compliance.
                </p>
              </section>

              <section className="blog-section">
                <h4>Building a Safer Workplace</h4>
                <p>PPE works best when combined with proper safety practices, including:</p>
                <ul className="blog-list">
                  {safetyPractices.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p>
                  A strong safety culture helps reduce accidents and improve day-to-day operations.
                </p>
              </section>

              <section className="blog-section">
                <h4>Reliable PPE From Totalguard Workwear</h4>
                <p>
                  At Totalguard Workwear, we supply quality PPE, safety footwear, workwear, and
                  protective equipment designed for South African industries. From construction and
                  mining to warehousing and engineering, our products are built for comfort,
                  durability, and dependable protection.
                </p>
              </section>

              <div className="blog-closing">
                <p>
                  Quality PPE is an investment in your people and your business. When workers have
                  safety gear that fits properly and performs reliably, businesses benefit from safer
                  worksites, improved productivity, and stronger workplace safety standards.
                </p>
                <p>
                  Speak to Totalguard Workwear about PPE built for South African worksites.
                </p>
                <a href="#contact" className="btn">Discuss PPE Requirements</a>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

export default Blog;
